import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Typography,
  Button,
  FormControlLabel,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ListIcon from "@mui/icons-material/List";
import { EntityStatus } from "../../types";

export interface EntityAssetCardProps {
  title: string;
  subtitle?: string;
  status: string;
  metadata?: React.ReactNode;
  enabled: boolean;
  onToggleEnabled: () => void;
  onEdit: () => void;
  onView?: () => void;
  onDelete?: () => void;
  onViewRequests?: () => void;
  toggleLoading?: boolean;
}

const EntityAssetCard: React.FC<EntityAssetCardProps> = ({
  title,
  subtitle,
  status,
  metadata,
  enabled,
  onToggleEnabled,
  onEdit,
  onView,
  onDelete,
  onViewRequests,
  toggleLoading = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: 2 },
        }}
      >
        <CardContent sx={{ flex: 1, pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Chip
              label={status}
              size="small"
              color={status === EntityStatus.ACTIVE ? "success" : "default"}
            />
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography
            variant="h6"
            fontWeight={600}
            gutterBottom
            sx={{
              wordBreak: "break-word",
              ...(onView
                ? {
                    cursor: "pointer",
                    "&:hover": { color: "primary.main" },
                  }
                : {}),
            }}
            onClick={onView}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {subtitle}
            </Typography>
          )}

          {metadata && (
            <Box sx={{ mt: 1, mb: 1 }}>{metadata}</Box>
          )}
        </CardContent>

        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={enabled}
                onChange={onToggleEnabled}
                disabled={toggleLoading}
              />
            }
            label={
              <Typography variant="caption">
                {enabled ? "Enabled" : "Disabled"}
              </Typography>
            }
          />
          <Box>
            {onView && (
              <IconButton size="small" onClick={onView} aria-label="View">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={onEdit} aria-label="Edit">
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Card>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {onView && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onView();
            }}
          >
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> View
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onEdit();
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        {onViewRequests && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onViewRequests();
            }}
          >
            <ListIcon fontSize="small" sx={{ mr: 1 }} /> View requests
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setDeleteOpen(true);
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {onDelete && (
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete item?</DialogTitle>
          <DialogContent>
            Are you sure you want to delete &quot;{title}&quot;?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                setDeleteOpen(false);
                onDelete();
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default EntityAssetCard;
